import { goto, invalidate } from '$app/navigation';
import { page } from '$app/state';
import { PUBLIC_POCKETBASE_URL } from '$app/env/public';
import {
	COL_ANALYTICS_USERS,
	COL_API_KEYS,
	COL_CONVERSATION,
	COL_EVAL_FUNNEL,
	COL_INSTANCE,
	COL_USERS
} from '#src/constants/collections.ts';
import type { ApiKeyRecord, CreateApiKeyData } from '#src/types/api-key.ts';
import type { ConversationFilter, ConversationRecord } from '#src/types/conversation.ts';
import type { EvalFunnelRecord } from '#src/types/eval.ts';
import type { InstanceFeatureFlags, InstanceRecord } from '@astlai/common/types/instance';
import type { UserRecord, UserWithStats } from '#src/types/user.ts';
import PocketBase, { type ListResult } from 'pocketbase';

function isJwt(token: string): boolean {
	const parts = token.split('.');
	if (parts.length !== 3 || parts.some((p) => !p)) {
		return false;
	}
	try {
		const header = JSON.parse(atob(parts[0].replace(/-/g, '+').replace(/_/g, '/')));
		return header.typ === 'JWT' || typeof header.alg === 'string';
	} catch {
		return false;
	}
}

export class PbCoreApiService extends PocketBase {
	constructor(baseURL = PUBLIC_POCKETBASE_URL, tokenOrApiKey?: string) {
		super(baseURL);

		const apiKey = tokenOrApiKey && !isJwt(tokenOrApiKey) ? tokenOrApiKey : undefined;

		if (apiKey) {
			this.beforeSend = (url, options) => {
				options.headers = { ...options.headers, Authorization: `Bearer ${apiKey}` };
				options.credentials = 'omit';
				return { url, options };
			};
		} else {
			if (tokenOrApiKey) {
				this.authStore.save(tokenOrApiKey);
			}
			this.beforeSend = (url, options) => {
				options.credentials = 'include';
				return { url, options };
			};
		}
	}

	async updateConversation(id: string, update: Partial<ConversationRecord>) {
		await this.collection<ConversationRecord>(COL_CONVERSATION).update(id, update);
		await invalidate(`update:conversation:${id}`);
		await invalidate('update:conversations');
	}

	async deleteConvo(id: string) {
		await this.collection<ConversationRecord>(COL_CONVERSATION).delete(id);
		await invalidate('update:conversations');
		if (page.params?.conversationId === id) {
			goto('/');
		}
	}

	async fetchConversations(
		filter: ConversationFilter,
		{ page = 1, perPage = 20 }: { page?: number; perPage?: number } = {}
	): Promise<ListResult<ConversationRecord>> {
		const { ownerId, instanceId, type, query } = filter;

		const filterParts: string[] = [];
		const filterParams: Record<string, string> = {};

		if (ownerId) {
			filterParts.push('owner = {:ownerId}');
			filterParams.ownerId = ownerId;
		}
		if (instanceId) {
			filterParts.push('instance = {:instanceId}');
			filterParams.instanceId = instanceId;
		}
		if (type) {
			filterParts.push('type = {:type}');
			filterParams.type = type;
		}
		if (query) {
			filterParts.push('(title ~ {:q} || messages ~ {:q})');
			filterParams.q = query;
		}

		return this.collection<ConversationRecord>(COL_CONVERSATION).getList(page, perPage, {
			filter: this.filter(filterParts.join(' && '), filterParams),
			sort: '-updated',
			fields: 'id,title,model,updated,type,api_key,expand.api_key.name',
			expand: 'api_key'
		});
	}

	async updateMyself(changes: Partial<UserRecord>) {
		try {
			await this.collection<UserRecord>(COL_USERS).update(page.data.loggedInUser.id, changes);
			await invalidate('update:user');
		} catch (e: unknown) {
			if ((e as { status?: number })?.status === 404) {
				return;
			}
			throw e;
		}
	}

	async deleteMyAccount() {
		await this.collection<UserRecord>(COL_USERS).update(page.data.loggedInUser.id, {
			deleted_at: new Date().toISOString()
		});
	}

	async updateUser(userId: string, changes: Partial<UserRecord>): Promise<void> {
		await this.collection<UserRecord>(COL_USERS).update(userId, changes);
		await invalidate(`update:user-${userId}`);
	}

	async updateUserRole(userId: string, role: string): Promise<void> {
		await this.collection<UserRecord>(COL_USERS).update(userId, { role });
		await invalidate('update:users');
	}

	async updateUserPlan(userId: string, plan: string): Promise<void> {
		await this.collection<UserRecord>(COL_USERS).update(userId, { plan });
		await invalidate('update:users');
	}

	async deleteUser(userId: string): Promise<void> {
		await this.collection<UserRecord>(COL_USERS).update(userId, {
			deleted_at: new Date().toISOString()
		});
		await invalidate('update:users');
	}

	// Values are `any` (not Partial<InstanceRecord>'s field types) so file fields
	// like reference_image/logo can be set to a File — the SDK auto-converts to
	// multipart when a body value is a File/Blob.
	async updateInstance(instanceId: string, changes: Partial<Record<keyof InstanceRecord, any>>) {
		await this.collection<InstanceRecord>(COL_INSTANCE).update(instanceId, changes);
		await invalidate('update:instance');
	}

	async updateFeatureFlag<K extends keyof InstanceFeatureFlags>(
		instance: Pick<InstanceRecord, 'id' | 'feature_flags'>,
		key: K,
		value: InstanceFeatureFlags[K]
	) {
		await this.updateInstance(instance.id, {
			feature_flags: { ...instance.feature_flags, [key]: value }
		});
	}

	async trackViewState(viewStateKey: 'onboarding'): Promise<void>;
	async trackViewState(viewStateKey: 'conversation', subKey: string): Promise<void>;
	async trackViewState(viewStateKey: 'news', subKey: string): Promise<void>;
	async trackViewState(viewStateKey: keyof UserRecord['viewState'], subKey?: string) {
		const viewStateUpdate: UserRecord['viewState'] = {
			...(page.data.loggedInUser.viewState || {})
		};

		if (viewStateKey === 'conversation') {
			viewStateUpdate.conversation = {
				...(viewStateUpdate.conversation || {}),
				[subKey!]: new Date()
			};
		} else if (viewStateKey === 'news') {
			viewStateUpdate.news = { ...(viewStateUpdate.news || {}), [subKey!]: new Date() };
		} else {
			viewStateUpdate.onboarding = new Date();
		}

		await this.updateMyself({ viewState: viewStateUpdate });
	}

	async requestResetPassword(email: string): Promise<void> {
		await this.collection(COL_USERS).requestPasswordReset(email);
	}

	async saveNewPassword(password: string, passwordConfirm: string, token: string): Promise<void> {
		await this.collection(COL_USERS).confirmPasswordReset(token, password, passwordConfirm);
	}

	async createApiKey(data: CreateApiKeyData): Promise<ApiKeyRecord> {
		const record = await this.collection<ApiKeyRecord>(COL_API_KEYS).create({
			...data,
			is_active: true,
			usage_count: 0
		});
		await invalidate('update:api_keys');
		return record;
	}

	async deleteApiKey(id: string): Promise<void> {
		await this.collection<ApiKeyRecord>(COL_API_KEYS).delete(id);
		await invalidate('update:api_keys');
	}

	async getInstance(instanceId: string): Promise<InstanceRecord> {
		return this.collection<InstanceRecord>(COL_INSTANCE).getOne(instanceId, {
			fields: 'id, name, welcome_message, feature_flags, default_model'
		});
	}

	/** Resolves the instance tied to this service's own authenticated principal (API key or cookie session) — used by the embeddable widgets, which authenticate purely via an instance-scoped key and never know an instance id upfront. */
	async getMyInstance(): Promise<InstanceRecord> {
		return this.send('/api/my-instance', { method: 'GET' });
	}

	// eval_funnel's ListRule is superuser-only, so this must be called via
	// locals.pbSu, not the cookie-authed client.
	async fetchEvalFunnels(): Promise<EvalFunnelRecord[]> {
		return this.collection<EvalFunnelRecord>(COL_EVAL_FUNNEL).getFullList({
			sort: 'name',
			fields: 'id, name'
		});
	}

	async createInstance(data: Partial<InstanceRecord>): Promise<InstanceRecord> {
		const record = await this.collection<InstanceRecord>(COL_INSTANCE).create(data);
		await invalidate('update:instances');
		return record;
	}

	/** A copy of the instance with its own reference images and logo, gallery and auto-auth off (backend/routes/admin_instances.go). */
	async duplicateInstance(
		instanceId: string,
		data: { name: string; slug: string }
	): Promise<Pick<InstanceRecord, 'id' | 'name' | 'slug'>> {
		const record = await this.send(`/api/admin/instances/${instanceId}/duplicate`, {
			method: 'POST',
			body: JSON.stringify(data)
		});
		await invalidate('update:instances');
		return record;
	}

	async addCreditGrant(userId: string, amount: number, note: string): Promise<void> {
		await this.send(`/api/admin/users/${userId}/credit-grants`, {
			method: 'POST',
			body: JSON.stringify({ amount, note })
		});
	}

	/**
	 * `organization`: an organization id, `none` for users without one, or empty for everyone.
	 */
	async fetchUsersWithStats(
		instanceId: string,
		search: string,
		organization: string,
		page: number,
		perPage: number,
		sort: string
	): Promise<{ users: UserWithStats[]; totalItems: number }> {
		const filters = [`instance = {:instanceId}`];
		const filterParams: Record<string, string> = { instanceId };

		if (organization) {
			filters.push('organization = {:organization}');
			filterParams.organization = organization === 'none' ? '' : organization;
		}

		if (search.trim()) {
			const words = search.trim().split(/\s+/);
			words.forEach((word, i) => {
				filters.push(`(first_name ~ {:q${i}} || last_name ~ {:q${i}} || email ~ {:q${i}})`);
				filterParams[`q${i}`] = word;
			});
		}

		const result = await this.collection<UserWithStats>(COL_ANALYTICS_USERS).getList(
			page,
			perPage,
			{
				filter: this.filter(filters.join(' && '), filterParams),
				sort,
				fields:
					'id,first_name,last_name,email,avatar,role,plan,organization,user_created,conversation_count,message_count,token_count,input_token_count,output_token_count,cache_creation_token_count,cache_read_token_count,credit_count,credits_used_this_month,exempt_credits_this_month,grant_credits_used,total_grants_received,grant_balance,active_briefing_count,stripe_subscription_id,stripe_customer_id,last_activity'
			}
		);

		return { totalItems: result.totalItems, users: result.items };
	}
}

export const pbCoreApiService = new PbCoreApiService();
